import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getGameFlow } from '../services/gameFlow';
import { getTeams } from '../services/teams';
import { getTeamSeasonStats } from '../services/teamSeasonStats';
import { mergeMatchSnapshots, isStaleLiveFeed } from '../data/matchState';
import { deriveMatchSetState } from '../data/matchState';
import { reconcileRallySnapshot, isSummaryCorrection } from '../data/scoreReconciliation.js';
import { broadcastLabel, gameBroadcastLabel } from '../data/broadcast';
import { canonicalTeamSlug } from '../data/teamIdentity';
import TeamLogo from './TeamLogo';
import '../styles/match-center-flow.css';

const LIVE_REFRESH_INTERVAL = 20_000;

function extractGameId(game) {
  const directId = String(game?.gameId || game?.id || '').trim();
  if (/^[1-9]\d*$/.test(directId)) return directId;
  return String(game?.gameUrl || game?.detailsApiUrl || '').match(/\/game\/(\d+)/i)?.[1] || '';
}

function firstString(...values) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function normalizeTeamSlug(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getTeamProfileSlug(team) {
  return firstString(
    team?.slug,
    team?.teamSeo,
    team?.team_seo,
    team?.seo,
    team?.urlName,
    team?.url_name,
    normalizeTeamSlug(team?.name)
  );
}

export function findCurrentTeamRecord(teams, team, minimumDate = '') {
  const slug = canonicalTeamSlug(getTeamProfileSlug(team) || team?.name);
  const match = (Array.isArray(teams) ? teams : []).find((candidate) => {
    const candidateSlug = canonicalTeamSlug(getTeamProfileSlug(candidate) || candidate?.name);
    return Boolean(slug && candidateSlug === slug);
  });

  if (minimumDate && (!match?.finalRecord?.throughDate || match.finalRecord.throughDate < minimumDate)) return '';
  return firstString(match?.record, match?.finalRecord?.label);
}

export function matchRecordSeason(game, details) {
  for (const match of [game, details]) {
    for (const value of [match?.scheduleDate, match?.startDate, match?.dateKey, match?.date, match?.startTimeIso]) {
      const year = String(value || '').match(/^(20\d{2})-/)?.[1];
      if (year) return Number(year);
    }
  }
  return null;
}

function cleanSetScores(value) {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 5).map((score) => {
    if (score === null || score === undefined || score === '') return null;
    const number = Number(score);
    return Number.isFinite(number) && number >= 0 ? number : null;
  });
}

function readCurrentSet(...values) {
  for (const value of values) {
    if (Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= 5) {
      return Number(value);
    }

    const label = String(value || '').trim();
    const match =
      label.match(/(?:set|period)\s*([1-5])\b/i) ||
      label.match(/\b([1-5])(?:st|nd|rd|th)?\s*(?:set|period)\b/i);
    const setNumber = Number(match?.[1]);

    if (Number.isInteger(setNumber) && setNumber >= 1 && setNumber <= 5) {
      return setNumber;
    }
  }

  return null;
}

function getCurrentSet(game, details) {
  const explicit = readCurrentSet(
    details?.currentSet,
    details?.match?.currentSet,
    details?.match?.currentPeriod,
    details?.game?.currentPeriod,
    details?.game?.period,
    details?.raw?.game?.currentPeriod,
    details?.raw?.game?.period,
    game?.currentSet,
    game?.currentPeriod,
    game?.statusLabel
  );

  if (explicit) return explicit;

  const eventSets = (Array.isArray(details?.events) ? details.events : [])
    .map((event) => Number(event?.setNumber))
    .filter((setNumber) => Number.isInteger(setNumber) && setNumber >= 1 && setNumber <= 5);

  if (eventSets.length) return Math.max(...eventSets);

  const awayScores = cleanSetScores(game?.away?.setScores);
  const homeScores = cleanSetScores(game?.home?.setScores);
  const startedSets = Math.max(awayScores.length, homeScores.length);
  if (!startedSets) return null;

  const lastIndex = startedSets - 1;
  const away = awayScores[lastIndex];
  const home = homeScores[lastIndex];
  const target = startedSets === 5 ? 15 : 25;
  const completed = Number.isFinite(away) && Number.isFinite(home) &&
    Math.max(away, home) >= target && Math.abs(away - home) >= 2;

  return completed ? Math.min(5, startedSets + 1) : startedSets;
}


export function mergeGameWithDetails(game, details) {
  const detailMatch = details?.match || {};
  const metadata = getMatchMetadata(game, details);

  return mergeMatchSnapshots(game, {
    ...game,
    terminalState: detailMatch.terminalState ?? game?.terminalState,
    statusVerifiedAt: detailMatch.statusVerifiedAt || game?.statusVerifiedAt,
    statusNote: detailMatch.statusNote ?? game?.statusNote,
    metadataVerifiedAt: detailMatch.metadataVerifiedAt || game?.metadataVerifiedAt,
    fetchedAt: detailMatch.fetchedAt || details?.fetchedAt || game?.fetchedAt,
    authoritative: detailMatch.authoritative === true,
    sourceUpdatedAt: detailMatch.sourceUpdatedAt,
    sourceUrl: detailMatch.sourceUrl || game?.sourceUrl,
    scoreSource: detailMatch.scoreSource || game?.scoreSource,
    network: metadata.network || game?.network || '',
    venue: metadata.venue !== 'Venue TBD' ? metadata.venue : game?.venue || '',
    status: detailMatch.status || game?.status,
    statusLabel: detailMatch.statusLabel || game?.statusLabel,
    rawStatus: detailMatch.rawStatus || detailMatch.status || game?.rawStatus,
    currentSet: readCurrentSet(
      detailMatch.currentSet,
      detailMatch.currentPeriod,
      details?.currentSet,
      game?.currentSet,
      game?.currentPeriod
    ),
    currentPeriod: firstString(
      detailMatch.currentPeriod,
      details?.currentSet,
      game?.currentPeriod
    ),
    away: {
      ...(game?.away || {}),
      ...(detailMatch.away || {}),
      name: firstString(game?.away?.name, detailMatch.away?.name, 'Away'),
      score: detailMatch.away?.score ?? game?.away?.score ?? null,
      setScores: detailMatch.away?.setScores || [],
      record: firstString(detailMatch.away?.record, game?.away?.record),
      conference: firstString(game?.away?.conference, detailMatch.away?.conference),
      rank: game?.away?.rank ?? detailMatch.away?.rank ?? null,
      slug: firstString(game?.away?.slug, detailMatch.away?.slug),
    },
    home: {
      ...(game?.home || {}),
      ...(detailMatch.home || {}),
      name: firstString(game?.home?.name, detailMatch.home?.name, 'Home'),
      score: detailMatch.home?.score ?? game?.home?.score ?? null,
      setScores: detailMatch.home?.setScores || [],
      record: firstString(detailMatch.home?.record, game?.home?.record),
      conference: firstString(game?.home?.conference, detailMatch.home?.conference),
      rank: game?.home?.rank ?? detailMatch.home?.rank ?? null,
      slug: firstString(game?.home?.slug, detailMatch.home?.slug),
    },
  });
}

export function mergeGameFlowDetails(previous, incoming) {
  if (!previous) return incoming ? { ...incoming, summaryMatch: incoming.match,
    ...reconcileRallySnapshot(incoming.match, incoming.events) } : incoming;
  if (!incoming) return previous;

  const incomingEvents = Array.isArray(incoming.events) ? incoming.events : [];
  const oldSummary = previous.summaryMatch || previous.match;
  const staleSummary = Date.parse(incoming.match?.fetchedAt || '') < Date.parse(oldSummary?.fetchedAt || '');
  const rejectedSets = new Set(staleSummary ? [] : incoming.feedIntegrity?.contradictorySets || []);
  const previousEvents = (previous.events || []).filter((event) => !rejectedSets.has(event.setNumber));
  const eventProgress = (rows) => (rows || []).reduce((best, row) => Math.max(best, (row.setNumber || 0) * 1000 + (row.awayScore || 0) + (row.homeScore || 0)), 0);
  const scoreSignature = match => JSON.stringify([match?.away?.setScores, match?.home?.setScores]);
  const correction = !staleSummary && (isSummaryCorrection(oldSummary, incoming.match) ||
    previous.correctedSummary === scoreSignature(incoming.match));
  const acceptEvents = !staleSummary && incomingEvents.length && (correction || eventProgress(incomingEvents) >= eventProgress(previousEvents));
  const feedIntegrity = acceptEvents ? incoming.feedIntegrity : rejectedSets.size ? {
    ...previous.feedIntegrity,
    ...incoming.feedIntegrity,
    incomplete: true,
    contradictorySets: [...new Set([...(previous.feedIntegrity?.contradictorySets || []), ...rejectedSets])],
  } : previous.feedIntegrity;
  const incomingTeamStats = Array.isArray(incoming.teamStats) ? incoming.teamStats : [];
  const incomingPlayers = Array.isArray(incoming.playerTables) ? incoming.playerTables : [];
  const summaryMatch = staleSummary ? oldSummary : mergeMatchSnapshots(oldSummary, incoming.match);
  const reconciled = reconcileRallySnapshot(summaryMatch, acceptEvents ? incomingEvents : previousEvents, { correction });

  return {
    ...previous,
    ...incoming,
    summaryMatch,
    correctedSummary: correction ? scoreSignature(summaryMatch) : staleSummary ? previous.correctedSummary : '',
    ...reconciled,
    feedIntegrity,
    flowSource: acceptEvents ? incoming.flowSource : previous.flowSource,
    teamStats: [...new Map([...(previous.teamStats || []), ...incomingTeamStats].map((row) => [row.key || row.label, row])).values()],
    playerTables: incomingPlayers.length ? incomingPlayers : previous.playerTables || [],
    availability: {
      ...(previous.availability || {}),
      ...(incoming.availability || {}),
      game: Boolean(previous.availability?.game || incoming.availability?.game),
      playByPlay: Boolean(previous.availability?.playByPlay || incoming.availability?.playByPlay),
      scoringSummary: Boolean(previous.availability?.scoringSummary || incoming.availability?.scoringSummary),
      teamStats: Boolean(previous.availability?.teamStats || incoming.availability?.teamStats),
      boxscore: Boolean(previous.availability?.boxscore || incoming.availability?.boxscore),
    },
    routeSources: {
      ...(previous.routeSources || {}),
      ...(incoming.routeSources || {}),
    },
    raw: {
      ...(previous.raw || {}),
      ...(incoming.raw || {}),
    },
  };
}

function unwrapGamePayload(value) {
  const root = value?.data || value || {};
  return root?.gamecenterGame ||
    root?.gameCenterGame ||
    root?.gameCenter?.game ||
    root?.game ||
    root?.contest ||
    root;
}

function parseMatchDateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = new Date(value < 1_000_000_000_000 ? value * 1000 : value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const text = String(value).trim();
  if (!text || /^\d{1,2}:\d{2}(?:\s*[AP]M)?(?:\s*[A-Z]{2,4})?$/i.test(text)) return null;

  if (/^\d{10,13}$/.test(text)) {
    const epoch = Number(text);
    const parsed = new Date(text.length === 10 ? epoch * 1000 : epoch);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const usDate = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usDate) {
    return new Date(Number(usDate[3]), Number(usDate[1]) - 1, Number(usDate[2]), 12);
  }

  const isoDate = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
  if (isoDate && !text.includes('T')) {
    return new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]), 12);
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateWithYear(...values) {
  for (const value of values) {
    const parsed = parseMatchDateValue(value);
    if (parsed) {
      return parsed.toLocaleDateString([], {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/New_York',
      });
    }

    const text = String(value || '').trim();
    if (/\b(19|20)\d{2}\b/.test(text)) return text;
  }
  return '';
}

function formatMatchTime(...values) {
  for (const value of values) {
    if (!value) continue;
    const text = String(value).trim();
    if (!text) continue;
    if (/^\d{1,2}:\d{2}(?:\s*[AP]M)?(?:\s*[A-Z]{2,4})?$/i.test(text)) return text;
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'America/New_York',
        timeZoneName: 'short',
      });
    }
  }
  return '';
}

function findNestedDateValues(value, depth = 0, seen = new WeakSet()) {
  if (!value || typeof value !== 'object' || depth > 5 || seen.has(value)) return [];
  seen.add(value);
  const values = [];
  const dateKeys = new Set([
    'startdate',
    'startdatetime',
    'contestdate',
    'gamedate',
    'scheduleddate',
    'date',
    'datelabel',
  ]);

  Object.entries(value).forEach(([key, child]) => {
    const normalizedKey = key.toLowerCase().replace(/[^a-z]/g, '');
    if (dateKeys.has(normalizedKey) && (typeof child === 'string' || typeof child === 'number')) {
      values.push(child);
    }
    if (child && typeof child === 'object') {
      values.push(...findNestedDateValues(child, depth + 1, seen));
    }
  });

  return values;
}

export function getMatchMetadata(game, details) {
  const raw = unwrapGamePayload(details?.game);
  const contest = Array.isArray(raw?.contests) && raw.contests.length
    ? raw.contests[0]
    : raw?.contest && typeof raw.contest === 'object'
      ? raw.contest
      : raw;
  const contestLocation = contest?.location && typeof contest.location === 'object'
    ? contest.location
    : {};
  const rawLocation = raw?.location && typeof raw.location === 'object'
    ? raw.location
    : {};
  const gameLocation = game?.location && typeof game.location === 'object'
    ? game.location
    : {};
  const cityState = [
    firstString(contestLocation?.city, rawLocation?.city, gameLocation?.city),
    firstString(
      contestLocation?.stateUsps,
      contestLocation?.stateCode,
      contestLocation?.state,
      rawLocation?.stateUsps,
      rawLocation?.state,
      gameLocation?.stateUsps,
      gameLocation?.state
    ),
  ].filter(Boolean).join(', ');

  const venue = firstString(
    game?.metadataVerifiedAt ? game?.venue : '',
    typeof contest?.venue === 'string' ? contest.venue : '',
    contest?.venue?.name,
    contest?.venue?.fullName,
    contest?.venue?.venueName,
    typeof contest?.location === 'string' ? contest.location : '',
    contestLocation?.venue,
    contestLocation?.venueName,
    contestLocation?.facility,
    contestLocation?.facilityName,
    contestLocation?.arena,
    contestLocation?.stadium,
    contestLocation?.name,
    contestLocation?.fullName,
    contest?.site?.venue,
    contest?.site?.name,
    typeof raw?.venue === 'string' ? raw.venue : '',
    raw?.venue?.name,
    raw?.venue?.fullName,
    rawLocation?.venue,
    rawLocation?.venueName,
    rawLocation?.facility,
    rawLocation?.name,
    details?.match?.venue,
    details?.match?.location?.venue,
    details?.match?.location?.name,
    typeof game?.venue === 'string' ? game.venue : '',
    game?.venue?.name,
    game?.venue?.fullName,
    typeof game?.location === 'string' ? game.location : '',
    gameLocation?.venue,
    gameLocation?.venueName,
    gameLocation?.facility,
    gameLocation?.name,
    cityState,
    'Venue TBD'
  );
  const network = broadcastLabel(
    gameBroadcastLabel(game),
    gameBroadcastLabel(raw),
    contest?.network,
    contest?.broadcasterName,
    contest?.broadcast,
    contest?.media,
    contest?.video,
    contest?.stream,
    contest?.watchInfo,
    raw?.network,
    raw?.broadcast,
    raw?.media,
    raw?.video,
    raw?.stream,
    game?.network,
    game?.broadcast,
    game?.media,
    game?.video
  );
  const date = formatDateWithYear(
    game?.startTimeIso,
    contest?.startDate,
    contest?.contestDate,
    contest?.gameDate,
    contest?.date,
    contest?.startDateTime,
    contest?.startTimeEpoch,
    contest?.startEpoch,
    contest?.dateEpoch,
    contest?.timestamp,
    raw?.startDate,
    raw?.gameDate,
    raw?.date,
    raw?.startDateTime,
    raw?.startTimeEpoch,
    raw?.startEpoch,
    raw?.dateEpoch,
    raw?.timestamp,
    game?.startDate,
    game?.gameDate,
    game?.date,
    game?.dateLabel,
    game?.dateKey,
    game?.scheduleDate,
    game?.scheduledDate,
    game?.startDateTime,
    game?.startTimeEpoch,
    game?.startEpoch,
    game?.dateEpoch,
    game?.timestamp,
    ...findNestedDateValues(raw)
  );
  const timeValue = formatMatchTime(
    game?.startTimeIso,
    contest?.startTimeDisplay,
    contest?.startTimeDetail,
    contest?.timeDisplay,
    contest?.time,
    contest?.startDateTime,
    contest?.startDate,
    raw?.startTimeDisplay,
    raw?.timeDisplay,
    raw?.time,
    raw?.startDateTime,
    raw?.startDate,
    game?.timeLabel,
    game?.time,
    game?.startTime
  );
  const time = game?.status !== 'scheduled' && (!timeValue || /\b(?:TBA|TBD)\b/i.test(timeValue)) ? '' : timeValue || 'Time TBD';

  return { venue, network, date, time };
}

function TeamSummary({
  team,
  side,
  neutralSite,
  winner,
  onOpenTeam,
  setCount,
  opponentSetScores,
  setState,
}) {
  const canOpen = Boolean(getTeamProfileSlug(team));
  const setScores = cleanSetScores(team?.setScores);
  const opponentScores = cleanSetScores(opponentSetScores);

  return (
    <section
      className={`match-center-team match-center-team-${side}${winner ? ' winner' : ''}`}
      aria-label={`${team?.name || 'Team'} summary`}
    >
      <button
        type="button"
        className="match-center-team-logo-button"
        disabled={!canOpen}
        onClick={() => onOpenTeam(team)}
        aria-label={`Open ${team?.name || 'team'} profile`}
      >
        <TeamLogo team={team} variant="match-center" />
      </button>

      <div className="match-center-team-copy">
        <span className="match-center-team-label">{neutralSite ? 'Neutral site' : side === 'away' ? 'Away' : 'Home'}</span>
        <h3>
          {team?.rank ? <span className="match-center-team-rank">#{team.rank}</span> : null}
          <button
            type="button"
            className="match-center-team-name-button"
            disabled={!canOpen}
            onClick={() => onOpenTeam(team)}
          >
            {team?.name || 'TBD'}
          </button>
        </h3>
        <div className="match-center-team-meta">
          {team?.record ? <span>{team.record}</span> : null}
          {team?.conference ? <span className="conference-pill">{team.conference}</span> : null}
        </div>
      </div>

      {setCount > 0 ? (
        <div className="match-center-team-set-block" aria-label={`${team?.name || 'Team'} set scores`}>
          <div className="set-score-labels" aria-hidden="true">
            {Array.from({ length: setCount }, (_, index) => (
              <span className={setState?.sets?.[index]?.state === 'in-progress' ? 'current' : ''} key={`${side}-label-${index + 1}`}>
                {index + 1}
              </span>
            ))}
          </div>
          <div className="match-center-team-set-scores">
            {Array.from({ length: setCount }, (_, index) => {
              const score = setScores[index];
              const opponentScore = opponentScores[index];
              const setNumber = index + 1;
              const setIsCurrent = setState?.sets?.[index]?.state === 'in-progress';
              const setComplete = setState?.sets?.[index]?.state === 'completed';
              const wonSet = setComplete && Number.isInteger(score) && Number.isInteger(opponentScore) && score > opponentScore;

              return (
                <span
                  key={`${side}-set-${setNumber}`}
                  className={`set-score-cell${wonSet ? ' won' : ''}${setIsCurrent ? ' current' : ''}${!Number.isFinite(score) ? ' empty' : ''}`}
                  aria-label={`Set ${setNumber}: ${score ?? 'not available'}${wonSet ? ', won' : setIsCurrent ? ', in progress' : ''}`}
                >
                  {score ?? '—'}
                </span>
              );
            })}
          </div>
        </div>
      ) : null}

      <strong className="match-center-team-score">{team?.score ?? '—'}</strong>
    </section>
  );
}


function buildSetScoreFallbackEvents(game, { setState } = {}) {
  const awayScores = cleanSetScores(game?.away?.setScores);
  const homeScores = cleanSetScores(game?.home?.setScores);
  const setCount = Math.max(awayScores.length, homeScores.length);

  return Array.from({ length: setCount }, (_, index) => {
    const awayScore = awayScores[index];
    const homeScore = homeScores[index];
    if (!Number.isInteger(awayScore) || !Number.isInteger(homeScore)) return null;
    const teamSide = awayScore > homeScore ? 'away' : homeScore > awayScore ? 'home' : '';
    const winnerName = teamSide === 'away' ? game?.away?.name : teamSide === 'home' ? game?.home?.name : '';
    const setIsCurrent = setState?.sets?.[index]?.state === 'in-progress';
    const setCompleted = setState?.sets?.[index]?.state === 'completed';
    return {
      id: `set-result-${index + 1}`,
      sequence: index,
      setNumber: index + 1,
      description: setIsCurrent
        ? winnerName
          ? `${winnerName} leads the active set.`
          : 'The active set is tied.'
        : setCompleted && winnerName
          ? `${winnerName} won Set ${index + 1}.`
          : setCompleted
            ? `Set ${index + 1} ended.`
            : setState?.sets?.[index]?.state === 'paused' ? `Last available score in Set ${index + 1}; match ${game.statusLabel?.toLowerCase() || 'paused'}.`
            : game.status === 'final' ? `Set ${index + 1} points unavailable.` : `Set ${index + 1} has not started.`,
      teamSide,
      awayScore,
      homeScore,
      scoreText: `${awayScore}–${homeScore}`,
    };
  }).filter(Boolean);
}

function getSetOptions(events, game) {
  const eventSets = events
    .map((event) => event.setNumber)
    .filter((value) => Number.isInteger(value) && value > 0);
  const scoreboardSets = Math.max(
    game?.away?.setScores?.length || 0,
    game?.home?.setScores?.length || 0
  );
  const maximum = Math.max(1, scoreboardSets, ...eventSets);
  return Array.from({ length: maximum }, (_, index) => index + 1);
}

function deriveFlowSummary(events) {
  const scored = events
    .filter((event) => Number.isInteger(event.awayScore) && Number.isInteger(event.homeScore))
    .sort((left, right) => Number(left.sequence || 0) - Number(right.sequence || 0));
  let previousLeader = '';
  let leadChanges = 0;
  let timesTied = 0;
  let awayLargest = 0;
  let homeLargest = 0;
  let runSide = '';
  let runLength = 0;
  let awayLongest = 0;
  let homeLongest = 0;

  scored.forEach((event) => {
    const difference = event.awayScore - event.homeScore;
    const leader = difference > 0 ? 'away' : difference < 0 ? 'home' : '';
    if (difference === 0 && event.awayScore > 0) timesTied += 1;
    if (leader && previousLeader && leader !== previousLeader) leadChanges += 1;
    if (leader) previousLeader = leader;
    awayLargest = Math.max(awayLargest, difference);
    homeLargest = Math.max(homeLargest, -difference);

    if (event.teamSide) {
      if (event.teamSide === runSide) runLength += 1;
      else {
        runSide = event.teamSide;
        runLength = 1;
      }
      if (runSide === 'away') awayLongest = Math.max(awayLongest, runLength);
      if (runSide === 'home') homeLongest = Math.max(homeLongest, runLength);
    }
  });

  return {
    final: scored.at(-1) || null,
    leadChanges,
    timesTied,
    largestLead: awayLargest >= homeLargest
      ? { side: 'away', value: awayLargest }
      : { side: 'home', value: homeLargest },
    longestRun: awayLongest >= homeLongest
      ? { side: 'away', value: awayLongest }
      : { side: 'home', value: homeLongest },
  };
}

function oppositeSide(side) {
  return side === 'away' ? 'home' : side === 'home' ? 'away' : '';
}

function normalizeRosterName(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildRosterSideLookup(playerTables) {
  const entries = [];

  (Array.isArray(playerTables) ? playerTables : []).forEach((table) => {
    if (table?.side !== 'away' && table?.side !== 'home') return;
    (Array.isArray(table?.rows) ? table.rows : []).forEach((row) => {
      const name = normalizeRosterName(Array.isArray(row) ? row[0] : '');
      if (name.length < 4) return;
      entries.push({ name, side: table.side });
    });
  });

  return entries.sort((left, right) => right.name.length - left.name.length);
}

function inferSideFromRoster(description, rosterLookup) {
  const normalized = normalizeRosterName(description);
  if (!normalized) return '';
  return rosterLookup.find((entry) => normalized.includes(entry.name))?.side || '';
}

function inferObservedRallySide(event, awayDelta, homeDelta, rosterLookup) {
  if (event?.teamSide === 'away' || event?.teamSide === 'home') return event.teamSide;
  if (awayDelta > 0 && homeDelta === 0) return 'away';
  if (homeDelta > 0 && awayDelta === 0) return 'home';
  return inferSideFromRoster(event?.description, rosterLookup);
}

function inferOpeningServer(event, rallySide, rosterLookup) {
  const description = String(event?.description || '').toLowerCase();
  const namedSide = inferSideFromRoster(event?.description, rosterLookup);

  if (/service\s+ace|\bace\b/.test(description)) {
    return namedSide || rallySide;
  }

  if (/service\s+error|serving\s+error/.test(description)) {
    return namedSide || oppositeSide(rallySide);
  }

  return '';
}

function buildRallySequence(events, playerTables = []) {
  const scored = events
    .filter((event) => Number.isInteger(event.awayScore) && Number.isInteger(event.homeScore))
    .sort((left, right) => Number(left.sequence || 0) - Number(right.sequence || 0));
  const unique = [];
  const seenScores = new Set();

  scored.forEach((event) => {
    const signature = `${event.awayScore}-${event.homeScore}`;
    if (seenScores.has(signature)) return;
    seenScores.add(signature);
    unique.push(event);
  });

  const rosterLookup = buildRosterSideLookup(playerTables);
  const rallies = [];
  let previousAway = 0;
  let previousHome = 0;
  let previousRallySide = '';
  let missingTransitions = 0;

  unique.forEach((event) => {
    const awayDelta = event.awayScore - previousAway;
    const homeDelta = event.homeScore - previousHome;

    if (awayDelta === 0 && homeDelta === 0) return;

    const onePointTransition =
      (awayDelta === 1 && homeDelta === 0) ||
      (awayDelta === 0 && homeDelta === 1);
    const observedSide = inferObservedRallySide(event, awayDelta, homeDelta, rosterLookup);

    if (onePointTransition) {
      const side = awayDelta === 1 ? 'away' : 'home';
      const isOpeningRally = previousAway === 0 && previousHome === 0;
      const serverSide = previousRallySide ||
        (isOpeningRally ? inferOpeningServer(event, side, rosterLookup) : '');

      rallies.push({
        side,
        serverSide,
        awayScore: event.awayScore,
        homeScore: event.homeScore,
        previousAway,
        previousHome,
      });
      previousRallySide = side;
    } else {
      // Keep the last observed rally as an anchor when possible. This lets the
      // next verified point establish serving possession even if the feed
      // omitted one or more opening or intermediate rallies.
      missingTransitions += Math.max(1, awayDelta + homeDelta);
      previousRallySide = observedSide;
    }

    previousAway = Math.max(previousAway, event.awayScore);
    previousHome = Math.max(previousHome, event.homeScore);
  });

  return {
    rallies,
    complete: missingTransitions === 0,
    missingTransitions,
  };
}

function formatPercent(wins, opportunities) {
  if (!opportunities) return '—';
  return `${Math.round((wins / opportunities) * 100)}%`;
}

function deriveAdvancedFlowStats(events, setNumber, playerTables = []) {
  const { rallies, complete, missingTransitions } = buildRallySequence(events, playerTables);
  const stats = {
    away: {
      sideoutWins: 0,
      receiveOpportunities: 0,
      breakpointWins: 0,
      serveOpportunities: 0,
      runsThreePlus: 0,
      longestRun: 0,
      latePoints: 0,
    },
    home: {
      sideoutWins: 0,
      receiveOpportunities: 0,
      breakpointWins: 0,
      serveOpportunities: 0,
      runsThreePlus: 0,
      longestRun: 0,
      latePoints: 0,
    },
  };
  let runSide = '';
  let runLength = 0;
  let excludedServeReceiveRallies = 0;
  const lateThreshold = setNumber === 5 ? 10 : 20;

  rallies.forEach((rally) => {
    if (rally.side === runSide) {
      runLength += 1;
    } else {
      if (runSide && runLength >= 3) stats[runSide].runsThreePlus += 1;
      runSide = rally.side;
      runLength = 1;
    }

    stats[rally.side].longestRun = Math.max(stats[rally.side].longestRun, runLength);

    if (Math.max(rally.previousAway, rally.previousHome) >= lateThreshold - 1) {
      stats[rally.side].latePoints += 1;
    }

    // Sideout percentage is receive attempts won divided by verified receive
    // attempts. Under rally scoring, the previous rally winner serves next.
    // When the opening server is explicitly identified by an ace/error or by
    // a roster match, that first rally can also be counted. Otherwise only
    // that ambiguous rally is excluded and calculation resumes immediately.
    const server = rally.serverSide;
    if (server !== 'away' && server !== 'home') {
      excludedServeReceiveRallies += 1;
      return;
    }

    const receiver = oppositeSide(server);
    stats[server].serveOpportunities += 1;
    stats[receiver].receiveOpportunities += 1;

    if (rally.side === server) stats[server].breakpointWins += 1;
    else stats[receiver].sideoutWins += 1;
  });

  if (runSide && runLength >= 3) stats[runSide].runsThreePlus += 1;

  ['away', 'home'].forEach((side) => {
    stats[side].sideoutPct = formatPercent(
      stats[side].sideoutWins,
      stats[side].receiveOpportunities
    );
    stats[side].breakpointPct = formatPercent(
      stats[side].breakpointWins,
      stats[side].serveOpportunities
    );
  });

  const verifiedServeReceiveRallies =
    stats.away.serveOpportunities + stats.home.serveOpportunities;

  return {
    hasRallies: rallies.length > 0,
    serveReceiveReliable: verifiedServeReceiveRallies > 0,
    serveReceivePartial: !complete || excludedServeReceiveRallies > 0,
    excludedServeReceiveRallies,
    missingTransitions,
    verifiedServeReceiveRallies,
    stats,
    lateThreshold,
  };
}

function sideLabel(side, game) {
  if (side === 'away') return game?.away?.name || 'Away';
  if (side === 'home') return game?.home?.name || 'Home';
  return '—';
}

function FlowMetric({ label, value, detail }) {
  return (
    <div className="match-flow-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      {detail ? <small>{detail}</small> : null}
    </div>
  );
}

function ScoreProgression({ events, game, completed = false }) {
  const scored = events
    .filter((event) => Number.isInteger(event.awayScore) && Number.isInteger(event.homeScore))
    .sort((left, right) => Number(left.sequence || 0) - Number(right.sequence || 0));

  if (scored.length < 2) {
    return (
      <div className="match-flow-chart-empty">
        The score graph appears when NCAA provides running point totals for this set.
      </div>
    );
  }

  const snapshots = [
    { awayScore: 0, homeScore: 0 },
    ...scored,
  ].map((event) => ({
    ...event,
    differential: event.awayScore - event.homeScore,
  }));
  const maximumDifference = Math.max(
    3,
    ...snapshots.map((event) => Math.abs(event.differential))
  );
  const width = 100;
  const height = 54;
  const horizontalPadding = 5;
  const verticalPadding = 6;
  const chartHeight = height - verticalPadding * 2;
  const x = (index) => horizontalPadding +
    (index / Math.max(snapshots.length - 1, 1)) * (width - horizontalPadding * 2);
  const y = (difference) => verticalPadding +
    ((maximumDifference - difference) / (maximumDifference * 2)) * chartHeight;
  const zeroY = y(0);

  const segments = [];
  const addSegment = (x1, y1, x2, y2, side) => {
    segments.push({ x1, y1, x2, y2, side });
  };

  for (let index = 1; index < snapshots.length; index += 1) {
    const previous = snapshots[index - 1];
    const current = snapshots[index];
    const x1 = x(index - 1);
    const x2 = x(index);
    const y1 = y(previous.differential);
    const y2 = y(current.differential);
    const previousSign = Math.sign(previous.differential);
    const currentSign = Math.sign(current.differential);

    if (previousSign && currentSign && previousSign !== currentSign) {
      const crossingRatio = Math.abs(previous.differential) /
        (Math.abs(previous.differential) + Math.abs(current.differential));
      const crossingX = x1 + (x2 - x1) * crossingRatio;
      addSegment(x1, y1, crossingX, zeroY, previousSign > 0 ? 'away' : 'home');
      addSegment(crossingX, zeroY, x2, y2, currentSign > 0 ? 'away' : 'home');
      continue;
    }

    const activeSign = currentSign || previousSign;
    addSegment(
      x1,
      y1,
      x2,
      y2,
      activeSign > 0 ? 'away' : activeSign < 0 ? 'home' : 'tie'
    );
  }

  const last = snapshots.at(-1);
  const lastSide = last.differential > 0 ? 'away' : last.differential < 0 ? 'home' : 'tie';
  const status = last.differential > 0
    ? `${game?.away?.name || 'Away'} ${completed ? 'won by' : 'leads by'} ${last.differential}`
    : last.differential < 0
      ? `${game?.home?.name || 'Home'} ${completed ? 'won by' : 'leads by'} ${Math.abs(last.differential)}`
      : 'The set is tied';

  return (
    <figure className="match-flow-chart match-flow-difference-chart">
      <div className="match-flow-chart-legend" aria-hidden="true">
        <span className="away">+ {game?.away?.name || 'Away'}</span>
        <span className="tie">Tied</span>
        <span className="home">− {game?.home?.name || 'Home'}</span>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`Score differential for ${game?.away?.name || 'away'} and ${game?.home?.name || 'home'}. ${status}.`}
      >
        <line x1={horizontalPadding} x2={width - horizontalPadding} y1={zeroY} y2={zeroY} className="axis tie-axis" />
        <line x1={horizontalPadding} x2={width - horizontalPadding} y1={y(maximumDifference)} y2={y(maximumDifference)} className="guide" />
        <line x1={horizontalPadding} x2={width - horizontalPadding} y1={y(-maximumDifference)} y2={y(-maximumDifference)} className="guide" />
        {segments.map((segment, index) => (
          <line
            key={`${segment.side}-${index}`}
            x1={segment.x1}
            y1={segment.y1}
            x2={segment.x2}
            y2={segment.y2}
            className={`difference-segment ${segment.side}`}
          />
        ))}
        <circle
          cx={x(snapshots.length - 1)}
          cy={y(last.differential)}
          r="1.35"
          className={`difference-end ${lastSide}`}
        />
      </svg>
      <figcaption>{completed ? status : `Latest available rally: ${status}`}</figcaption>
    </figure>
  );
}

function GameFlowPanel({ details, game, selectedSet, onSelectSet, live, currentSet, setState }) {
  const sourceEvents = Array.isArray(details?.events) ? details.events : [];
  const fallbackEvents = sourceEvents.length
    ? []
    : buildSetScoreFallbackEvents(game, { setState });
  const events = sourceEvents.length ? sourceEvents : fallbackEvents;
  const setScoreFallback = !sourceEvents.length && fallbackEvents.length > 0;
  const setOptions = getSetOptions(events, game);
  const effectiveSet = setOptions.includes(selectedSet) ? selectedSet : setOptions[0];
  const filteredEvents = events.filter((event) => event.setNumber === effectiveSet);
  const displayedEvents = [...filteredEvents].reverse();
  const summary = deriveFlowSummary(filteredEvents);
  const advanced = deriveAdvancedFlowStats(filteredEvents, effectiveSet, details?.playerTables);
  const latestId = filteredEvents.at(-1)?.id;
  const latestEvent = filteredEvents.at(-1);
  const flowRevision = [
    details?.fetchedAt || '',
    effectiveSet,
    filteredEvents.length,
    latestEvent?.sequence || '',
    latestEvent?.awayScore ?? '',
    latestEvent?.homeScore ?? '',
  ].join(':');
  const selectedSetState = setState?.sets?.[effectiveSet - 1];
  const selectedSetIsCurrent = selectedSetState?.state === 'in-progress';
  const selectedSetCompleted = selectedSetState?.state === 'completed';
  const authoritativeAway = game?.away?.setScores?.[effectiveSet - 1];
  const authoritativeHome = game?.home?.setScores?.[effectiveSet - 1];
  const rallyMismatch = summary.final && (
    Number(summary.final.awayScore) !== Number(authoritativeAway) ||
    Number(summary.final.homeScore) !== Number(authoritativeHome)
  );
  const setLeader = Number.isFinite(authoritativeAway) && Number.isFinite(authoritativeHome)
    ? authoritativeAway > authoritativeHome
      ? 'away'
      : authoritativeHome > authoritativeAway
        ? 'home'
        : ''
    : '';

  return (
    <section className="match-center-panel match-flow-panel">
      {details?.scoreSyncNotice ? <p role="status" className="match-flow-feed-warning">{details.scoreSyncNotice}</p> : null}
      {isStaleLiveFeed(game) ? <p role="status" className="match-flow-feed-warning">This older match is still marked live by its source. Its final result is unconfirmed; refreshing does not verify that the feed is current.</p> : null}
      {game.statusNote || ['suspended', 'unconfirmed'].includes(game.status) ? <p role="status" className="match-flow-feed-warning">{game.statusNote || 'Match paused. Showing the last available score.'}</p> : null}
      <div className="match-center-panel-heading match-flow-heading">
        <div className="match-flow-heading-copy">
          <span>{setScoreFallback ? 'Set results' : 'Point-by-point'}</span>
          <div className="match-flow-heading-row">
            <h3>Game flow</h3>
            <div className="match-flow-set-selector" role="group" aria-label="Select set">
              {setOptions.map((setNumber) => (
                <button
                  type="button"
                  key={setNumber}
                  className={`${effectiveSet === setNumber ? 'active' : ''}${live && currentSet === setNumber ? ' is-current' : ''}`}
                  aria-pressed={effectiveSet === setNumber}
                  onClick={() => onSelectSet(setNumber)}
                >
                  Set {setNumber}
                </button>
              ))}
            </div>
          </div>
        </div>
        {live && details?.fetchedAt ? (
          <small>
            Live refresh{' '}
            {new Date(details.fetchedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </small>
        ) : null}
      </div>

      {filteredEvents.length ? (
        <>
          {details?.feedIntegrity?.incomplete || rallyMismatch ? (
            <div className="match-flow-integrity-notice" role="status">
              Feed may be incomplete or behind the match summary. The graph shows only available rallies; rejected rows and missing rallies are not invented.
            </div>
          ) : null}
          <div className="match-flow-set-summary">
            <div>
              <span>Set {effectiveSet}</span>
              <strong>
                {`${authoritativeAway ?? '—'}–${authoritativeHome ?? '—'}`}
              </strong>
            </div>
            <p>
              {selectedSetCompleted
                ? `${sideLabel(selectedSetState?.winner || setLeader, game)} won Set ${effectiveSet}.`
                : selectedSetState?.state === 'paused' ? `Last available score in Set ${effectiveSet}; match ${game.statusLabel?.toLowerCase() || 'paused'}.`
                : summary.final
                  ? selectedSetIsCurrent
                  ? setLeader
                    ? `${sideLabel(setLeader, game)} leads the active set.`
                    : 'The active set is tied.'
                  : game.status === 'final' ? `Set ${effectiveSet} points unavailable.` : `Set ${effectiveSet} has not started.`
                : 'Final set score'}
            </p>
          </div>

          {!setScoreFallback ? (
            <ScoreProgression key={flowRevision} events={filteredEvents} game={game} completed={selectedSetCompleted && !rallyMismatch} />
          ) : null}

          {!setScoreFallback && summary.final ? <div className="match-flow-metrics">
            <FlowMetric
              label="Largest lead"
              value={summary.largestLead.value || '—'}
              detail={summary.largestLead.value ? sideLabel(summary.largestLead.side, game) : ''}
            />
            <FlowMetric
              label="Longest run"
              value={summary.longestRun.value || '—'}
              detail={summary.longestRun.value ? sideLabel(summary.longestRun.side, game) : ''}
            />
            <FlowMetric label="Lead changes" value={summary.leadChanges} />
            <FlowMetric label="Ties" value={summary.timesTied} />
          </div> : null}

          {!setScoreFallback && advanced.hasRallies ? (
            <section className="match-flow-advanced" aria-labelledby="advanced-flow-title">
              <div className="match-flow-subheading">
                <div>
                  <span>Serve and receive</span>
                  <h4 id="advanced-flow-title">Advanced flow</h4>
                </div>
                <small>Set {effectiveSet}</small>
              </div>
              <p className="mobile-table-hint">Swipe the table to see all flow statistics.</p>
              <div
                className="match-flow-advanced-table"
                role="table"
                tabIndex={0}
                aria-label={`Advanced flow statistics for set ${effectiveSet}`}
              >
                <div className="match-flow-advanced-row header" role="row">
                  <span role="columnheader">Team</span>
                  <span role="columnheader">Sideout</span>
                  <span role="columnheader">Breakpoint</span>
                  <span role="columnheader">3+ runs</span>
                  <span role="columnheader">Longest</span>
                  <span role="columnheader">Late pts</span>
                </div>
                {['away', 'home'].map((side) => (
                  <div className="match-flow-advanced-row" role="row" key={side}>
                    <strong role="cell">{sideLabel(side, game)}</strong>
                    <span role="cell">
                      {advanced.serveReceiveReliable ? advanced.stats[side].sideoutPct : '—'}
                    </span>
                    <span role="cell">
                      {advanced.serveReceiveReliable ? advanced.stats[side].breakpointPct : '—'}
                    </span>
                    <span role="cell">{advanced.stats[side].runsThreePlus}</span>
                    <span role="cell">{advanced.stats[side].longestRun}</span>
                    <span role="cell">{advanced.stats[side].latePoints}</span>
                  </div>
                ))}
              </div>
              <p className="match-flow-definition">
                Sideout percentage is the percentage of receive attempts successfully converted.
                Breakpoint percentage is the percentage of service attempts converted into points.
                Late points begin at {advanced.lateThreshold}.
                {!advanced.serveReceiveReliable
                  ? ' Sideout and breakpoint are unavailable because serving possession could not be established.'
                  : advanced.serveReceivePartial
                    ? ' Opening or missing rallies before serving possession could be verified were excluded.'
                    : ''}
              </p>
            </section>
          ) : null}

          <ol className="match-flow-events">
            {displayedEvents.map((event) => (
              <li
                key={event.id}
                className={`${event.teamSide || 'neutral'}${live && event.id === latestId ? ' latest' : ''}${event.feedGapBefore ? ' has-feed-gap' : ''}`}
              >
                {event.feedGapBefore ? <small className="match-flow-gap-label">Feed gap</small> : null}
                <span className="match-flow-event-marker" aria-hidden="true" />
                <div className="match-flow-event-copy">
                  <div>
                    <strong>Set {event.setNumber || effectiveSet}</strong>
                    {event.clock ? <small>{event.clock}</small> : null}
                  </div>
                  <p>{event.description}</p>
                </div>
                <span className="match-flow-event-score">{event.scoreText || 'Point'}</span>
              </li>
            ))}
          </ol>
        </>
      ) : (
        <div className="match-center-unavailable match-flow-unavailable">
          {live && selectedSetIsCurrent
            ? 'Awaiting verified live rally data. Available scores remain in the match header; missing scores are shown as dashes.'
            : 'Point-by-point scoring is not available for this set. The set score remains in the match header when NCAA publishes it.'}
        </div>
      )}
    </section>
  );
}

const PLAYER_STAT_DEFINITIONS = [
  { abbreviation: 'K', label: 'Kills', aliases: ['k', 'kill', 'kills'] },
  { abbreviation: 'E', label: 'Attack errors', aliases: ['e', 'error', 'errors', 'attack error', 'attack errors'] },
  { abbreviation: 'TA', label: 'Total attacks', aliases: ['ta', 'att', 'attempts', 'attack attempts', 'total attacks'] },
  { abbreviation: 'PCT', label: 'Hitting percentage', aliases: ['pct', 'hit pct', 'hit%', 'hitting percentage'] },
  { abbreviation: 'A', label: 'Assists', aliases: ['a', 'ast', 'assist', 'assists'] },
  { abbreviation: 'SA', label: 'Service aces', aliases: ['sa', 'ace', 'aces', 'service ace', 'service aces'] },
  { abbreviation: 'SE', label: 'Service errors', aliases: ['se', 'service error', 'service errors'] },
  { abbreviation: 'D', label: 'Digs', aliases: ['d', 'dig', 'digs'] },
  { abbreviation: 'RE', label: 'Reception errors', aliases: ['re', 'reception error', 'reception errors'] },
  { abbreviation: 'BS', label: 'Block solos', aliases: ['bs', 'block solo', 'block solos', 'solo blocks'] },
  { abbreviation: 'BA', label: 'Block assists', aliases: ['ba', 'block assist', 'block assists', 'assisted blocks'] },
  { abbreviation: 'TB', label: 'Total blocks', aliases: ['tb', 'blk', 'blocks', 'total blocks'] },
  { abbreviation: 'BE', label: 'Block errors', aliases: ['be', 'block error', 'block errors'] },
  { abbreviation: 'BHE', label: 'Ball-handling errors', aliases: ['bhe', 'ball handling errors', 'ball-handling errors'] },
  { abbreviation: 'PTS', label: 'Points', aliases: ['pts', 'point', 'points'] },
  { abbreviation: 'SP', label: 'Sets played', aliases: ['sp', 'sets played'] },
];

function normalizePlayerStatHeader(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function getPlayerStatDefinitions(headers) {
  const definitions = [];
  const seen = new Set();

  (Array.isArray(headers) ? headers.slice(1) : []).forEach((header) => {
    const normalized = normalizePlayerStatHeader(header);
    const definition = PLAYER_STAT_DEFINITIONS.find((item) =>
      item.aliases.includes(normalized)
    );
    if (!definition || seen.has(definition.abbreviation)) return;
    seen.add(definition.abbreviation);
    definitions.push({ ...definition, abbreviation: String(header).trim() });
  });

  return definitions;
}

export function splitPlayerDisplayName(value) {
  const name = String(value || '')
    .trim()
    .replace(/\s+/g, ' ');

  if (!name) {
    return {
      first: '—',
      last: '',
      full: '—',
    };
  }

  if (name.includes(',')) {
    const [last, ...firstParts] =
      name.split(',');
    const first = firstParts
      .join(',')
      .trim();

    return {
      first: first || last.trim(),
      last: first ? last.trim() : '',
      full: name,
    };
  }

  const parts = name.split(' ');

  if (parts.length === 1) {
    return {
      first: parts[0],
      last: '',
      full: name,
    };
  }

  const suffixPattern =
    /^(?:jr\.?|sr\.?|ii|iii|iv|v)$/i;
  const lastStart =
    parts.length > 2 &&
    suffixPattern.test(
      parts[parts.length - 1]
    )
      ? parts.length - 2
      : parts.length - 1;

  return {
    first: parts
      .slice(0, lastStart)
      .join(' '),
    last: parts
      .slice(lastStart)
      .join(' '),
    full: name,
  };
}

function MatchComparisonTable({ awayName, homeName, rows }) {
  return (
    <div className="match-team-stats">
      <div className="match-team-stats-header">
        <span>{awayName || 'Away'}</span>
        <strong>Statistic</strong>
        <span>{homeName || 'Home'}</span>
      </div>
      {rows.map((item) => {
        const quality = comparisonQuality(item.away, item.home, item.label);
        return (
          <div className="match-team-stat-row" key={item.key || item.label}>
            <strong className={quality === 'away' ? 'is-better' : ''}>{item.away || '—'}</strong>
            <span>{item.label}</span>
            <strong className={quality === 'home' ? 'is-better' : ''}>{item.home || '—'}</strong>
          </div>
        );
      })}
    </div>
  );
}

function StatsPanel({ details, game, statsView, onStatsView, playerSide, onPlayerSide }) {
  const comparisons = Array.isArray(details?.teamStats) ? details.teamStats : [];
  const playerTables = Array.isArray(details?.playerTables) ? details.playerTables : [];
  const hasTeamStats = comparisons.length > 0;
  const partialTeamStats = ['away', 'home'].some((side) => comparisons.filter((row) =>
    row.key !== 'scorePoints' && row[side] !== null && row[side] !== '' && Number.isFinite(Number(row[side]))
  ).length < 4);
  const hasPlayerStats = playerTables.length > 0;
  const effectiveView = statsView === 'players' ? 'players' : 'team';
  const availableSides = playerTables.map((table) => table.side).filter(Boolean);
  const effectiveSide = availableSides.includes(playerSide) ? playerSide : availableSides[0] || 'away';
  const selectedTable = playerTables.find((table) => table.side === effectiveSide) || playerTables[0];
  const playerStatDefinitions = getPlayerStatDefinitions(selectedTable?.headers);

  return (
    <section className="match-center-panel match-stats-panel">
      <div className="match-center-panel-heading">
        <div>
          <span>Match statistics</span>
          <h3>Stats</h3>
        </div>
      </div>

      {(hasTeamStats || hasPlayerStats) ? (
        <div className="match-stats-view-toggle" role="group" aria-label="Statistics view">
          <button
            type="button"
            className={effectiveView === 'team' ? 'active' : ''}
            aria-pressed={effectiveView === 'team'}
            onClick={() => onStatsView('team')}
          >
            Team stats
          </button>
          <button
            type="button"
            className={effectiveView === 'players' ? 'active' : ''}
            aria-pressed={effectiveView === 'players'}
            onClick={() => onStatsView('players')}
          >
            Player stats
          </button>
        </div>
      ) : null}

      {effectiveView === 'team' && !hasTeamStats ? (
        <div className="match-center-unavailable">
          NCAA has not published team statistics for this match.
        </div>
      ) : null}

      {effectiveView === 'players' && !hasPlayerStats ? (
        <div className="match-center-unavailable">
          NCAA has not published player statistics for this match.
        </div>
      ) : null}

      {effectiveView === 'team' && partialTeamStats ? <p className="match-center-unavailable">Only published match totals are shown. Full team statistics are not yet available.</p> : null}
      {effectiveView === 'team' && hasTeamStats ? (
        <MatchComparisonTable
          awayName={game?.away?.name}
          homeName={game?.home?.name}
          rows={comparisons}
        />
      ) : null}

      {effectiveView === 'players' && selectedTable ? (
        <>
          {availableSides.length > 1 ? (
            <div className="match-player-team-toggle" role="group" aria-label="Select player statistics team">
              {['away', 'home'].filter((side) => availableSides.includes(side)).map((side) => (
                <button
                  type="button"
                  key={side}
                  className={effectiveSide === side ? 'active' : ''}
                  aria-pressed={effectiveSide === side}
                  onClick={() => onPlayerSide(side)}
                >
                  {side === 'away' ? game?.away?.name || 'Away' : game?.home?.name || 'Home'}
                </button>
              ))}
            </div>
          ) : null}
          {game.status === 'live' || details?.statsProvisional ? <p className="match-stats-provisional" role="status">{game.status === 'live' ? 'Live statistics are provisional. SP (sets played) may include upstream placeholders and can change after the final box score.' : 'Final box score is being rechecked. These player statistics were collected while the match was live.'}</p> : null}
          <p className="mobile-table-hint">Swipe the table to see all player statistics.</p>
          <div className="match-boxscore-scroll" tabIndex={0} role="region" aria-label="Scrollable player statistics">
            <table className="match-boxscore-table">
              <thead>
                <tr>
                  <th scope="col">
                    {selectedTable.headers[0] || 'Player'}
                  </th>
                  <th
                    scope="col"
                    className="match-player-position-heading"
                  >
                    Pos
                  </th>
                  {selectedTable.headers.slice(1).map((header, index) => (
                    <th scope="col" key={`${header}-${index}`}>{header || '—'}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selectedTable.rows.map((row, rowIndex) => {
                  const playerName =
                    splitPlayerDisplayName(
                      row[0]
                    );
                  const position =
                    selectedTable.positions?.[
                      rowIndex
                    ] || '—';

                  return (
                    <tr key={`${selectedTable.id}-${rowIndex}`}>
                      <th
                        scope="row"
                        aria-label={playerName.full}
                      >
                        {selectedTable.jerseys?.[rowIndex] ? (
                          <small className="match-player-jersey" aria-label={`Jersey number ${selectedTable.jerseys[rowIndex]}`}>
                            #{selectedTable.jerseys[rowIndex]}
                          </small>
                        ) : null}
                        <span className="match-player-name">
                          <span className="match-player-first-name">
                            {playerName.first}
                          </span>
                          {playerName.last ? (
                            <span className="match-player-last-name">
                              {playerName.last}
                            </span>
                          ) : null}
                        </span>
                      </th>
                      <td className="match-player-position-cell">
                        <span className="match-player-position">
                          {position}
                        </span>
                      </td>
                      {row.slice(1).map((value, columnIndex) => (
                        <td key={columnIndex}>{value || '—'}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {playerStatDefinitions.length ? (
            <div className="match-player-stat-key" aria-label="Player statistic abbreviations">
              <strong>Stat key</strong>
              <div>
                {playerStatDefinitions.map((definition) => (
                  <span key={definition.abbreviation}>
                    <b>{definition.abbreviation}</b> = {definition.label}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function comparisonQuality(awayValue, homeValue, label = '') {
  const read = (value) => {
    const number = Number(String(value ?? '').replace(/[%,$]/g, ''));
    return Number.isFinite(number) ? number : null;
  };
  const away = read(awayValue);
  const home = read(homeValue);
  if (away === null || home === null || away === home) return '';
  const lowerIsBetter = /error|opponent hitting|loss/i.test(label);
  if (lowerIsBetter) return away < home ? 'away' : 'home';
  return away > home ? 'away' : 'home';
}

function SeasonComparison({ state, game }) {
  if (state.loading) {
    return <div className="match-center-skeleton" aria-label="Loading season comparison" role="status"><span /><span /><span /><span /></div>;
  }
  if (state.error || !state.away || !state.home) {
    return <div className="match-center-unavailable">Current-season comparison is not available yet.</div>;
  }
  const awayRows = new Map(state.away.teamStats.map((item) => [item.key, item]));
  const homeRows = new Map(state.home.teamStats.map((item) => [item.key, item]));
  const definitions = [
    ['hittingPercentage', 'Hitting percentage', false],
    ['opponentHittingPercentage', 'Opponent hitting percentage', false],
    ['kills', 'Kills per set', true],
    ['assists', 'Assists per set', true],
    ['serviceAces', 'Aces per set', true],
    ['digs', 'Digs per set', true],
    ['totalBlocks', 'Blocks per set', true],
    ['attackErrors', 'Attack errors per set', true],
  ];
  const rows = definitions.map(([key, label, useRate]) => {
    const away = awayRows.get(key);
    const home = homeRows.get(key);
    return { key, label, away: useRate ? away?.perSet : away?.value, home: useRate ? home?.perSet : home?.value };
  }).filter((row) => row.away || row.home);

  return (
    <section className="match-center-panel match-stats-panel match-season-comparison">
      <div className="match-center-panel-heading"><div><span>Current season</span><h3>Team comparison</h3></div><small>Through {state.throughDate || 'latest available games'}</small></div>
      <MatchComparisonTable
        awayName={game?.away?.name}
        homeName={game?.home?.name}
        rows={[
          {
            key: 'record',
            label: 'Current record',
            away: game?.away?.record,
            home: game?.home?.record,
          },
          ...rows,
        ]}
      />
      <p className="match-comparison-note">Rate statistics are used so teams with different numbers of matches remain comparable.</p>
    </section>
  );
}

export default function MatchCenter({ game, onClose, onSnapshot }) {
  const closeButtonRef = useRef(null);
  const requestIdRef = useRef({ flow: 0, stats: 0 });
  const controllerRef = useRef({ flow: null, stats: null });
  const manuallySelectedSetRef = useRef(false);
  const statsRequestedForGameRef = useRef('');
  const gameRef = useRef(game);
  const gameId = useMemo(() => extractGameId(game), [game]);
  const sourceLive = game?.status === 'live';
  const matchLiveRef = useRef(sourceLive);
  if (sourceLive) matchLiveRef.current = true;
  if (game?.status === 'final') matchLiveRef.current = false;
  gameRef.current = game;
  const [activeTab, setActiveTab] = useState(game?.status === 'scheduled' ? 'comparison' : 'flow');
  const [selectedSet, setSelectedSet] = useState(1);
  const [statsView, setStatsView] = useState('team');
  const [playerSide, setPlayerSide] = useState('away');
  const [recordOverrides, setRecordOverrides] = useState({ away: '', home: '' });
  const [comparisonState, setComparisonState] = useState({ loading: false, away: null, home: null, error: '', throughDate: '' });
  const [state, setState] = useState({
    loading: true,
    refreshing: false,
    details: null,
    detailsGameId: '',
    error: '',
    refreshError: '',
    statsLoading: false,
    statsError: '',
  });

  const loadDetails = useCallback(({
    background = false,
    scope = 'flow',
    force = false,
  } = {}) => {
    const statsRequest = scope === 'stats';

    if (!gameId) {
      setState({
        loading: false,
        refreshing: false,
        details: null,
        detailsGameId: '',
        error: 'Detailed data is not available for this match.',
        refreshError: '',
        statsLoading: false,
        statsError: '',
      });
      return;
    }

    controllerRef.current[scope]?.abort();
    const controller = new AbortController();
    controllerRef.current[scope] = controller;
    const requestId = requestIdRef.current[scope] + 1;
    requestIdRef.current[scope] = requestId;

    setState((current) => ({
      ...(statsRequest || background ? current : { details: null, detailsGameId: '' }),
      loading: statsRequest ? current.loading : background ? current.loading : true,
      refreshing: statsRequest ? current.refreshing : background,
      error: statsRequest || background ? current.error : '',
      refreshError: statsRequest ? current.refreshError : '',
      statsLoading: statsRequest || current.statsLoading,
      statsError: statsRequest ? '' : current.statsError,
    }));

    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, statsRequest ? 15_000 : 10_000);

    getGameFlow({
      gameId,
      live: matchLiveRef.current,
      signal: controller.signal,
      game: gameRef.current,
      scope,
      force,
    })
      .then((details) => {
        window.clearTimeout(timeoutId);
        if (requestId !== requestIdRef.current[scope]) return;
        matchLiveRef.current = mergeMatchSnapshots(gameRef.current, details?.match)?.status === 'live';
        setState((current) => ({
          ...current,
          loading: statsRequest ? current.loading : false,
          refreshing: statsRequest ? current.refreshing : false,
          details: { ...mergeGameFlowDetails(current.details, details),
            ...(statsRequest ? { statsProvisional: details?.match?.status === 'live' ||
              (!details?.playerTables?.length && Boolean(current.details?.statsProvisional)) } : {}) },
          detailsGameId: gameId,
          error: statsRequest ? current.error : '',
          refreshError: statsRequest ? current.refreshError : '',
          statsLoading: statsRequest ? false : current.statsLoading,
          statsError: statsRequest ? '' : current.statsError,
        }));
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        if (!timedOut && (error?.name === 'AbortError' || requestId !== requestIdRef.current[scope])) return;
        if (requestId !== requestIdRef.current[scope]) return;
        const message = timedOut
          ? `${statsRequest ? 'Match statistics' : 'Match details'} took too long to load. Please try again.`
          : error?.message || `${statsRequest ? 'Match statistics' : 'Match details'} could not be loaded.`;
        setState((current) => ({
          ...current,
          loading: statsRequest ? current.loading : false,
          refreshing: statsRequest ? current.refreshing : false,
          error: statsRequest || background ? current.error : message,
          refreshError: !statsRequest && background ? message : current.refreshError,
          statsLoading: statsRequest ? false : current.statsLoading,
          statsError: statsRequest ? message : current.statsError,
        }));
      });
  }, [gameId]);

  useEffect(() => {
    statsRequestedForGameRef.current = '';
    loadDetails({ scope: 'flow' });
    return () => {
      controllerRef.current.flow?.abort();
      controllerRef.current.stats?.abort();
    };
  }, [loadDetails]);

  const autoRefreshLive = mergeMatchSnapshots(game,
    state.detailsGameId === gameId ? state.details?.match : null)?.status === 'live';

  useEffect(() => {
    if (!autoRefreshLive) return undefined;
    const refreshVisible = () => {
      if (document.visibilityState === 'visible') {
        loadDetails({ background: true, scope: 'flow', force: true });
        if (activeTab === 'stats') loadDetails({ background: true, scope: 'stats', force: true });
      }
    };
    const timer = window.setInterval(refreshVisible, LIVE_REFRESH_INTERVAL);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [autoRefreshLive, loadDetails, activeTab]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const safeDetails = state.detailsGameId === gameId ? state.details : null;
  const publishedSnapshotRef = useRef('');
  const detailMergedGame = useMemo(
    () => mergeGameWithDetails(game, safeDetails),
    [game, safeDetails]
  );
  const mergedGame = useMemo(() => ({
    ...detailMergedGame,
    away: {
      ...(detailMergedGame?.away || {}),
      record: recordOverrides.away || detailMergedGame?.away?.record || '',
    },
    home: {
      ...(detailMergedGame?.home || {}),
      record: recordOverrides.home || detailMergedGame?.home?.record || '',
    },
  }), [detailMergedGame, recordOverrides.away, recordOverrides.home]);

  useEffect(() => {
    if (safeDetails && typeof onSnapshot === 'function') {
      const signature = JSON.stringify([gameId, mergedGame.status, mergedGame.currentSet, mergedGame.away, mergedGame.home, mergedGame.venue, mergedGame.network]);
      if (publishedSnapshotRef.current === signature) return;
      publishedSnapshotRef.current = signature;
      onSnapshot(mergedGame);
    }
  }, [mergedGame, onSnapshot, safeDetails]);
  const live = mergedGame?.status === 'live';
  const observedLiveRef = useRef({ gameId, live });
  useEffect(() => {
    const previous = observedLiveRef.current;
    observedLiveRef.current = { gameId, live };
    if (previous.gameId === gameId && previous.live && mergedGame.status === 'final') {
      matchLiveRef.current = false;
      loadDetails({ background: true, scope: 'stats', force: true });
    }
  }, [gameId, live, mergedGame.status, loadDetails]);
  const matchSetState = useMemo(
    () => deriveMatchSetState(mergedGame),
    [mergedGame]
  );
  const recordSeason = matchRecordSeason(game, mergedGame);
  const recordAwaySlug = getTeamProfileSlug(mergedGame?.away);
  const recordHomeSlug = getTeamProfileSlug(mergedGame?.home);

  useEffect(() => {
    setRecordOverrides({ away: '', home: '' });

    if (live) return undefined;

    if (recordSeason !== 2026 || !recordAwaySlug || !recordHomeSlug) return undefined;

    const controller = new AbortController();
    getTeams({ season: recordSeason, signal: controller.signal, force: true })
      .then((directory) => {
        if (controller.signal.aborted) return;
        const current = gameRef.current;
        const completedDate = current?.status === 'final'
          ? firstString(current.date, current.scheduleDate, current.dateKey, new URLSearchParams(window.location.search).get('date')).slice(0, 10)
          : '';
        setRecordOverrides({
          away: findCurrentTeamRecord(directory?.teams, { slug: recordAwaySlug }, [completedDate, current?.away?.recordThroughDate || ''].sort().at(-1)),
          home: findCurrentTeamRecord(directory?.teams, { slug: recordHomeSlug }, [completedDate, current?.home?.recordThroughDate || ''].sort().at(-1)),
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          // Match Center keeps the detail-feed records when the directory refresh fails.
        }
      });

    return () => controller.abort();
  }, [gameId, live, recordSeason, recordAwaySlug, recordHomeSlug]);

  useEffect(() => {
    if (game?.status !== 'scheduled') {
      setComparisonState({ loading: false, away: null, home: null, error: '', throughDate: '' });
      setActiveTab('flow');
      return undefined;
    }
    setActiveTab('comparison');
    const season = Number(firstString(game?.scheduleDate, game?.startDate, game?.dateKey).slice(0, 4)) || 2026;
    const awaySlug = getTeamProfileSlug(game?.away);
    const homeSlug = getTeamProfileSlug(game?.home);
    if (!awaySlug || !homeSlug) return undefined;
    const controller = new AbortController();
    setComparisonState({ loading: true, away: null, home: null, error: '', throughDate: '' });
    Promise.all([
      getTeamSeasonStats({ season, team: awaySlug, signal: controller.signal }),
      getTeamSeasonStats({ season, team: homeSlug, signal: controller.signal }),
    ]).then(([away, home]) => setComparisonState({
      loading: false,
      away,
      home,
      error: '',
      throughDate: [away?.fetchedAt, home?.fetchedAt].filter(Boolean).sort().at(-1)?.slice(0, 10) || '',
    })).catch((error) => {
      if (error?.name !== 'AbortError') setComparisonState({ loading: false, away: null, home: null, error: error?.message || 'Comparison unavailable.', throughDate: '' });
    });
    return () => controller.abort();
  }, [gameId, game?.status]);
  const metadata = useMemo(
    () => getMatchMetadata(mergedGame, safeDetails),
    [mergedGame, safeDetails]
  );
  const awayWinner = mergedGame.status === 'final' && Number(mergedGame?.away?.score) > Number(mergedGame?.home?.score);
  const homeWinner = mergedGame.status === 'final' && Number(mergedGame?.home?.score) > Number(mergedGame?.away?.score);
  const currentSet = live ? matchSetState.activeSet : null;
  const setCount = Math.max(
    mergedGame?.away?.setScores?.length || 0,
    mergedGame?.home?.setScores?.length || 0,
    currentSet || 0
  );

  useEffect(() => {
    if (
      activeTab !== 'stats' ||
      !safeDetails ||
      state.statsLoading ||
      statsRequestedForGameRef.current === gameId
    ) {
      return;
    }

    statsRequestedForGameRef.current = gameId;
    loadDetails({ background: true, scope: 'stats' });
  }, [activeTab, gameId, loadDetails, safeDetails, state.statsLoading]);

  useEffect(() => {
    manuallySelectedSetRef.current = false;
    setSelectedSet(live ? deriveMatchSetState(game).activeSet || 1 : 1);
  }, [gameId, live]);

  useEffect(() => {
    const options = getSetOptions(safeDetails?.events || [], mergedGame);
    const preferredSet = live
      ? currentSet || matchSetState.lastCompletedSet || options.at(-1) || 1
      : 1;

    if (!manuallySelectedSetRef.current || !options.includes(selectedSet)) {
      setSelectedSet(options.includes(preferredSet) ? preferredSet : options[0] || 1);
    }
  }, [currentSet, live, matchSetState.lastCompletedSet, mergedGame, selectedSet, safeDetails]);

  const selectSet = useCallback((setNumber) => {
    manuallySelectedSetRef.current = true;
    setSelectedSet(setNumber);
  }, []);

  const refreshMatchCenter = useCallback(() => {
    loadDetails({ background: true, scope: 'flow', force: true });

    if (activeTab === 'stats') {
      statsRequestedForGameRef.current = gameId;
      loadDetails({ background: true, scope: 'stats', force: true });
    }
  }, [activeTab, gameId, loadDetails]);

  const openTeam = useCallback((team) => {
    const slug = getTeamProfileSlug(team);
    if (!slug) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('sport');
    url.searchParams.delete('match');
    url.searchParams.set('team', slug);
    url.hash = 'teams';
    window.history.pushState({}, '', url.toString());
    const navigationEvent = typeof PopStateEvent === 'function'
      ? new PopStateEvent('popstate')
      : new Event('popstate');
    window.dispatchEvent(navigationEvent);
  }, []);

  return (
    <div
      className="match-center-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <section className="match-center match-center-flow-enabled" role="dialog" aria-modal="true" aria-label="Match Center">
        <header className="match-center-header">
          <div className="match-center-header-copy">
            <div className="match-center-header-topline">
              <span>Match Center</span>
              <div
                className="match-center-header-meta"
                title={[metadata.date, metadata.time, metadata.venue, metadata.network].filter(Boolean).join(' • ')}
              >
                {live ? <span className="is-live">Live</span> : null}
                {metadata.date ? <span>{metadata.date}</span> : null}
                {metadata.time ? <span>{metadata.time}</span> : null}
                <span>{metadata.venue}</span>
                {metadata.network ? <span>{metadata.network}</span> : null}
              </div>
            </div>
            <h2>{mergedGame?.away?.name || 'Away'} vs. {mergedGame?.home?.name || 'Home'}</h2>
            <div className="match-center-header-score" aria-hidden="true">
              <strong>{mergedGame?.away?.score ?? '—'}</strong>
              <span>–</span>
              <strong>{mergedGame?.home?.score ?? '—'}</strong>
              {live ? (
                <span className="match-center-current-set">
                  {currentSet
                    ? `Set ${currentSet} live`
                    : matchSetState.betweenSets
                      ? `Set ${matchSetState.lastCompletedSet} ended`
                      : 'Live'}
                </span>
              ) : null}
            </div>
          </div>
          <div className="match-center-header-actions">
            <button
              type="button"
              className={`match-center-refresh${state.refreshing ? ' refreshing' : ''}`}
              onClick={refreshMatchCenter}
              disabled={state.loading || state.refreshing || state.statsLoading || !gameId}
              aria-label={state.refreshing || state.statsLoading ? 'Refreshing Match Center' : 'Refresh Match Center'}
            >
              <span aria-hidden="true">↻</span>
              <span>{state.refreshing || state.statsLoading ? 'Refreshing' : 'Refresh'}</span>
            </button>
            <button
              type="button"
              ref={closeButtonRef}
              className="match-center-close"
              onClick={onClose}
              aria-label="Close Match Center"
            >
              ×
            </button>
          </div>
        </header>

        <div className="match-center-scroll-area">
          <section className="match-center-scoreboard">
            <div className="match-center-matchup">
              <TeamSummary
                team={mergedGame.away}
                side="away"
                neutralSite={mergedGame.neutralSite}
                winner={awayWinner}
                onOpenTeam={openTeam}
                setCount={setCount}
                opponentSetScores={mergedGame.home?.setScores}
                setState={matchSetState}
              />
              <TeamSummary
                team={mergedGame.home}
                side="home"
                neutralSite={mergedGame.neutralSite}
                winner={homeWinner}
                onOpenTeam={openTeam}
                setCount={setCount}
                opponentSetScores={mergedGame.away?.setScores}
                setState={matchSetState}
              />
            </div>

          </section>

          <nav className="match-center-tabs" aria-label="Match Center sections">
            {game?.status === 'scheduled' ? (
              <button type="button" className={activeTab === 'comparison' ? 'active' : ''} aria-pressed={activeTab === 'comparison'} onClick={() => setActiveTab('comparison')}>
                Comparison
              </button>
            ) : null}
            <button
              type="button"
              className={activeTab === 'flow' ? 'active' : ''}
              aria-pressed={activeTab === 'flow'}
              onClick={() => setActiveTab('flow')}
            >
              Game Flow
            </button>
            <button
              type="button"
              className={activeTab === 'stats' ? 'active' : ''}
              aria-pressed={activeTab === 'stats'}
              onClick={() => setActiveTab('stats')}
            >
              Stats
            </button>
          </nav>

          <main className="match-center-content match-center-flow-content">
            {state.refreshError ? (
              <div className="match-center-refresh-note">Refresh failed. Showing the latest available match data.</div>
            ) : null}

            {(state.loading || state.details) && !safeDetails ? (
              <div className="match-center-skeleton" aria-label="Loading Match Center details" role="status">
                <span />
                <span />
                <span />
                <span />
              </div>
            ) : null}

            {!state.loading && state.error && !safeDetails ? (
              <div className="match-center-unavailable">
                <p>{state.error}</p>
                <button type="button" onClick={() => loadDetails()}>Retry</button>
              </div>
            ) : null}

            {activeTab === 'comparison' || safeDetails || (!state.loading && !state.details) ? (
              activeTab === 'comparison' ? (
                <SeasonComparison state={comparisonState} game={mergedGame} />
              ) : activeTab === 'flow' ? (
                <GameFlowPanel
                  details={safeDetails}
                  game={mergedGame}
                  selectedSet={selectedSet}
                  onSelectSet={selectSet}
                  live={live}
                  currentSet={currentSet}
                  setState={matchSetState}
                />
              ) : state.statsLoading && !safeDetails?.teamStats?.some((row) => row.key !== 'scorePoints') && !safeDetails?.availability?.boxscore ? (
                <div className="match-center-skeleton" aria-label="Loading match statistics" role="status">
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              ) : state.statsError && !safeDetails?.teamStats?.some((row) => row.key !== 'scorePoints') && !safeDetails?.availability?.boxscore ? (
                <div className="match-center-unavailable">
                  <p>{state.statsError}</p>
                  <button
                    type="button"
                    onClick={() => {
                      statsRequestedForGameRef.current = gameId;
                      loadDetails({ background: true, scope: 'stats', force: true });
                    }}
                  >
                    Retry statistics
                  </button>
                </div>
              ) : (
                <StatsPanel
                  details={safeDetails}
                  game={mergedGame}
                  statsView={statsView}
                  onStatsView={setStatsView}
                  playerSide={playerSide}
                  onPlayerSide={setPlayerSide}
                />
              )
            ) : null}
          </main>
        </div>
      </section>
    </div>
  );
}
